import {
  Check as CheckIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Link as LinkIcon,
  Settings as GeneralConfigIcon,
  WarningAmber as DeprecatedIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { AdminSectionPanel, AppChip } from "@/components/common";
import {
  configurationSections,
  type ConfigurationSectionKey,
  getConfigurationFieldTitle,
  type JsonRecord,
} from "@/pages/operations/configs/components/helpers";
import { CustomerConfig } from "@/services/features/CustomerService";
import { formatDecimal, formatInteger } from "@/utils/number";
import { formatDurationLong } from "@/utils/time";

const SectionAppIcon: React.FC<{ src: string }> = ({ src }) => (
  <Box
    component="img"
    src={src}
    alt=""
    sx={{
      width: 24,
      height: 24,
      display: "block",
      borderRadius: "50%",
      objectFit: "contain",
    }}
  />
);

const sectionIcons: Record<ConfigurationSectionKey, React.ReactNode> = {
  generalConfig: <GeneralConfigIcon />,
  shopperApp: <SectionAppIcon src="/images/app-icons/shopper_assist.svg" />,
  assistApp: <SectionAppIcon src="/images/app-icons/shopper_assist.svg" />,
  associateConfig: <SectionAppIcon src="/images/app-icons/associate.svg" />,
};

const isRecord = (value: unknown): value is JsonRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const localizedValueMapKeys = new Set([
  "defaultShopperName",
  "productDescriptionHeader",
  "greetingText",
]);

const isLocalizedValueMap = (
  value: JsonRecord,
  fieldKey?: string,
): value is Record<string, string> => {
  const values = Object.values(value);
  return (
    Boolean(fieldKey && localizedValueMapKeys.has(fieldKey)) &&
    values.length > 0 &&
    values.every((item) => typeof item === "string")
  );
};

const isStoreEnablement = (
  value: JsonRecord,
): value is { enabledForAll: boolean; enabledOnStore: number[] } =>
  typeof value.enabledForAll === "boolean" &&
  Array.isArray(value.enabledOnStore) &&
  value.enabledOnStore.every((storeId) => typeof storeId === "number");

const isImageUrl = (value: string) => {
  try {
    const { pathname, protocol } = new URL(value);
    return (
      (protocol === "https:" || protocol === "http:") &&
      /\.(avif|gif|jpe?g|png|svg|webp)$/i.test(pathname)
    );
  } catch {
    return false;
  }
};

const isDurationField = (fieldKey?: string) =>
  fieldKey === "timeoutSeconds" || fieldKey === "dressingRoomTimeoutSeconds";

const codeValueFieldKeys = new Set([
  "customerId",
  "requestListType",
  "locatorType",
  "scannerType",
  "customerEnum",
  "customerEnumId",
  "customerWideDevicePin",
]);

const CodeValue: React.FC<{ value: string | number }> = ({ value }) => (
  <AppChip
    tone="neutral"
    label={String(value)}
    size="small"
    sx={{
      justifySelf: "start",
      width: "fit-content",
      maxWidth: "100%",
      fontFamily: "monospace",
    }}
  />
);

const DurationValue: React.FC<{ seconds: number; locale: string }> = ({ seconds, locale }) => (
  <Stack spacing={0.5} alignItems="flex-start">
    <CodeValue value={seconds} />
    <Typography variant="caption" color="text.secondary">
      {formatDurationLong(seconds, locale)}
    </Typography>
  </Stack>
);

const BooleanChip: React.FC<{ value: boolean }> = ({ value }) => {
  const { t } = useTranslation();

  return (
    <AppChip
      tone={value ? "positive" : "negative"}
      size="small"
      icon={value ? <CheckIcon /> : <CloseIcon />}
      label={t(value ? "common.yes" : "common.no")}
      sx={{ justifySelf: "start", width: "fit-content" }}
    />
  );
};

const EnabledForAllStoresChip: React.FC = () => {
  const { t } = useTranslation();

  return (
    <AppChip
      tone="positive"
      size="small"
      icon={<CheckIcon />}
      label={t("operations.configs.editConfig.fields.enabledForAll")}
      sx={{ justifySelf: "start", width: "fit-content" }}
    />
  );
};

const DisabledForAllStoresChip: React.FC = () => {
  const { t } = useTranslation();

  return (
    <AppChip
      tone="negative"
      size="small"
      icon={<CloseIcon />}
      label={t("operations.configs.editConfig.fields.disabledForAll")}
      sx={{ justifySelf: "start", width: "fit-content" }}
    />
  );
};

const imageNameFromUrl = (value: string) => {
  try {
    const pathname = new URL(value).pathname;
    return pathname.split("/").filter(Boolean).pop() ?? value;
  } catch {
    return value;
  }
};

const UrlValue: React.FC<{ value: string; fieldKey?: string; fieldPath?: string }> = ({
  value,
  fieldKey,
  fieldPath,
}) => {
  const { t } = useTranslation();

  if (isImageUrl(value)) {
    const isBanner = fieldKey === "bannerImageUrl";
    const imageName = imageNameFromUrl(value);

    return (
      <Stack spacing={1} alignItems="flex-start">
        <Link
          href={value}
          target="_blank"
          rel="noreferrer"
          underline="hover"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, maxWidth: "100%" }}
        >
          <LinkIcon fontSize="small" aria-hidden="true" />
          <Typography component="span" noWrap>
            {imageName}
          </Typography>
        </Link>
        <Box
          component="img"
          src={value}
          alt={fieldPath ? getConfigurationFieldTitle(fieldPath, t) : ""}
          sx={{
            display: "block",
            width: isBanner ? "min(100%, 560px)" : "auto",
            maxWidth: isBanner ? "100%" : "min(100%, 280px)",
            height: "auto",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1.5,
            objectFit: isBanner ? "initial" : "contain",
            bgcolor: "common.white",
            backgroundImage:
              "/* @noflip */ conic-gradient(#e0e0e0 25%, transparent 0 50%, #e0e0e0 0 75%, transparent 0)",
            backgroundSize: "16px 16px",
          }}
        />
      </Stack>
    );
  }

  return (
    <Link href={value} target="_blank" rel="noreferrer" sx={{ overflowWrap: "anywhere" }}>
      {value}
    </Link>
  );
};

const ConfigSummaryValue: React.FC<{ value: unknown; fieldKey?: string; fieldPath?: string }> = ({
  value,
  fieldKey,
  fieldPath,
}) => {
  const { t, i18n } = useTranslation();

  if (value === null || value === undefined) {
    return <Typography color="text.secondary">{t("common.notAvailable")}</Typography>;
  }

  if (typeof value === "boolean") {
    return <BooleanChip value={value} />;
  }

  if (typeof value === "number") {
    if (isDurationField(fieldKey)) {
      return <DurationValue seconds={value} locale={i18n.language} />;
    }

    if (fieldKey && codeValueFieldKeys.has(fieldKey)) {
      return <CodeValue value={value} />;
    }

    return <Typography>{formatDecimal(value, i18n.language)}</Typography>;
  }

  if (typeof value === "string") {
    if (/^https?:\/\//i.test(value)) {
      return <UrlValue value={value} fieldKey={fieldKey} fieldPath={fieldPath} />;
    }

    if (fieldKey && codeValueFieldKeys.has(fieldKey)) {
      return <CodeValue value={value} />;
    }

    return <Typography sx={{ overflowWrap: "anywhere" }}>{value}</Typography>;
  }

  if (Array.isArray(value)) {
    return (
      <Stack spacing={1}>
        {value.map((item, index) => (
          <Box key={index} sx={{ borderInlineStart: "2px solid", borderColor: "divider", ps: 1.5 }}>
            <Typography color="text.secondary" sx={{ mb: 0.5 }}>
              {t("operations.configs.editConfig.fields.item", { number: index + 1 })}
            </Typography>
            <ConfigSummaryValue value={item} />
          </Box>
        ))}
      </Stack>
    );
  }

  if (isRecord(value)) {
    if (isStoreEnablement(value)) {
      if (value.enabledForAll) {
        return <EnabledForAllStoresChip />;
      }

      if (value.enabledOnStore.length === 0) {
        return <DisabledForAllStoresChip />;
      }

      return (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
          {value.enabledOnStore.map((storeId) => (
            <AppChip
              key={storeId}
              tone="neutral"
              size="small"
              label={formatInteger(storeId, i18n.language)}
            />
          ))}
        </Stack>
      );
    }

    if (isLocalizedValueMap(value, fieldKey)) {
      return (
        <Stack spacing={0.75}>
          {Object.entries(value).map(([locale, localizedValue]) => (
            <Box
              key={locale}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "minmax(96px, 0.25fr) minmax(0, 1fr)" },
                gap: 1,
              }}
            >
              <AppChip
                tone="primary"
                label={locale}
                size="small"
                sx={{
                  justifySelf: "start",
                  width: "fit-content",
                  fontFamily: "monospace",
                }}
              />
              <Typography sx={{ overflowWrap: "anywhere" }}>{localizedValue}</Typography>
            </Box>
          ))}
        </Stack>
      );
    }

    return <ConfigSummaryObject value={value} parentPath={fieldPath} />;
  }

  return null;
};

const ConfigSummaryObject: React.FC<{
  value: JsonRecord;
  excludedKeys?: ReadonlySet<string>;
  parentPath?: string;
}> = ({ value, excludedKeys, parentPath }) => {
  const { t } = useTranslation();

  return (
    <Stack spacing={0} divider={<Box sx={{ borderBottom: "1px solid", borderColor: "divider" }} />}>
      {Object.entries(value)
        .filter(([key, item]) => item !== undefined && !excludedKeys?.has(key))
        .map(([key, item]) => {
          const fieldPath = parentPath ? `${parentPath}.${key}` : key;

          return (
            <Box
              key={key}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "minmax(200px, 0.4fr) minmax(0, 1fr)" },
                gap: { xs: 0.5, sm: 2 },
                alignItems: "start",
                py: 1.5,
              }}
            >
              <Stack spacing={0.25}>
                <Typography color="text.secondary">
                  {getConfigurationFieldTitle(fieldPath, t)}
                </Typography>
                <Typography
                  component="pre"
                  variant="caption"
                  color="text.disabled"
                  sx={{ m: 0, fontFamily: "monospace" }}
                >
                  {key}
                </Typography>
              </Stack>
              <ConfigSummaryValue value={item} fieldKey={key} fieldPath={fieldPath} />
            </Box>
          );
        })}
    </Stack>
  );
};

export const ConfigSummary: React.FC<{ config: CustomerConfig }> = ({ config }) => {
  const { t } = useTranslation();
  const configuration = config as CustomerConfig & JsonRecord;

  return (
    <Stack spacing={3}>
      <AdminSectionPanel title={config.customerName}>
        <ConfigSummaryObject
          value={{
            customerId: config.customerId,
            customerName: config.customerName,
          }}
        />
      </AdminSectionPanel>
      {configurationSections.map((section) => {
        const value = configuration[section.key];
        if (!isRecord(value)) return null;
        const deprecatedValue = Object.fromEntries(
          Object.entries(value).filter(
            ([key, item]) => item !== undefined && section.deprecatedFields.includes(key),
          ),
        );
        const excludedKeys = new Set([...section.deprecatedFields, ...section.hiddenFields]);
        const hasDeprecatedValues = Object.keys(deprecatedValue).length > 0;

        return (
          <AdminSectionPanel
            key={section.key}
            title={t(section.titleKey)}
            icon={sectionIcons[section.key]}
          >
            <Stack spacing={2}>
              <ConfigSummaryObject
                value={value}
                excludedKeys={excludedKeys}
                parentPath={section.key}
              />
              {hasDeprecatedValues && (
                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "4px !important",
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <DeprecatedIcon color="warning" fontSize="small" />
                      <Typography fontWeight="fontWeightBold">
                        {t("operations.configs.editConfig.sections.deprecated")}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <ConfigSummaryObject value={deprecatedValue} parentPath={section.key} />
                  </AccordionDetails>
                </Accordion>
              )}
            </Stack>
          </AdminSectionPanel>
        );
      })}
    </Stack>
  );
};
